import React, { useState, useRef, useEffect } from 'react';
import './callStatusModal.css';
import { BolnaService } from '../../../../../services/bolnaService';

const CallStatusModal = ({ isVisible, onClose, callStatus, onRefresh, isLoading }) => {
    const [activeTab, setActiveTab] = useState('details');
    const [audioUrl, setAudioUrl] = useState(null);
    const [isLoadingAudio, setIsLoadingAudio] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [audioProgress, setAudioProgress] = useState(0);
    const [audioDuration, setAudioDuration] = useState(0);
    const [audioCurrentTime, setAudioCurrentTime] = useState(0);
    const [isSyncingRecording, setIsSyncingRecording] = useState(false);
    const [syncError, setSyncError] = useState('');
    const audioRef = useRef(null);

    // Load audio when recording tab is accessed or recording source changes
    useEffect(() => {
        if ((callStatus?.recordingPath || callStatus?.recordingUrl) && activeTab === 'recording' && !audioUrl) {
            loadAudioRecording();
        }
    }, [activeTab, callStatus?.recordingPath, callStatus?.recordingUrl]);

    // Cleanup audio URL on unmount
    useEffect(() => {
        return () => {
            if (audioUrl && audioUrl.startsWith('blob:')) {
                URL.revokeObjectURL(audioUrl);
            }
        };
    }, [audioUrl]);

    // Reset audio state when modal closes or callStatus changes
    useEffect(() => {
        if (!isVisible) {
            setAudioUrl(null);
            setIsPlaying(false);
            setAudioProgress(0);
            setAudioCurrentTime(0);
            setAudioDuration(0);
        }
    }, [isVisible]);

    const loadAudioRecording = async () => {
        // If no recording source available, return
        if (!callStatus?.recordingPath && !callStatus?.recordingUrl) return;

        setIsLoadingAudio(true);
        try {
            // Prefer recordingPath (authenticated download) if available
            if (callStatus?.recordingPath) {
                const response = await BolnaService.getCallRecording(callStatus.recordingPath);
                const blob = new Blob([response.data], { type: 'audio/mpeg' });
                const url = URL.createObjectURL(blob);
                setAudioUrl(url);
            } else if (callStatus?.recordingUrl) {
                // Use direct URL (e.g., S3 signed URL)
                setAudioUrl(callStatus.recordingUrl);
            }
        } catch (error) {
            console.error('Failed to load recording:', error);
            // Fallback to direct URL if authenticated download fails
            if (callStatus?.recordingUrl) {
                setAudioUrl(callStatus.recordingUrl);
            }
        } finally {
            setIsLoadingAudio(false);
        }
    };

    const togglePlayPause = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            const current = audioRef.current.currentTime;
            const duration = audioRef.current.duration;
            setAudioCurrentTime(current);
            setAudioProgress((current / duration) * 100);
        }
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setAudioDuration(audioRef.current.duration);
        }
    };

    const handleSeek = (e) => {
        if (audioRef.current) {
            const progressBar = e.currentTarget;
            const rect = progressBar.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const percentage = clickX / rect.width;
            audioRef.current.currentTime = percentage * audioRef.current.duration;
        }
    };

    const handleAudioEnded = () => {
        setIsPlaying(false);
        setAudioProgress(0);
        setAudioCurrentTime(0);
    };

    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleSyncRecording = async () => {
        if (!callStatus?.executionId) return;

        setIsSyncingRecording(true);
        setSyncError('');
        try {
            await BolnaService.syncRecording(callStatus.executionId);
            // Refresh call status to get updated recording info
            if (onRefresh) {
                await onRefresh();
            }
        } catch (error) {
            console.error('Failed to sync recording:', error);
            setSyncError('Failed to sync recording. Please try again.');
        } finally {
            setIsSyncingRecording(false);
        }
    };

    if (!isVisible) return null;

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return '#3BB765';
            case 'in_progress':
            case 'queued':
                return '#FFB700';
            case 'failed':
                return '#F35824';
            default:
                return '#516f90';
        }
    };

    const getStatusLabel = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return 'Completed';
            case 'in_progress':
                return 'In Progress';
            case 'queued':
                return 'Queued';
            case 'failed':
                return 'Failed';
            default:
                return status || 'Unknown';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const formatDuration = (seconds) => {
        if (!seconds) return '-';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        const truncatedSecs = Math.floor(secs * 100) / 100;

        return `${mins}m ${truncatedSecs.toFixed(2)}s`;
    };

    const getScoreColor = (score) => {
        if (score >= 80) return '#3BB765'; // Green - Excellent
        if (score >= 60) return '#FFB700'; // Yellow - Good
        if (score >= 40) return '#FF8C00'; // Orange - Average
        return '#F35824'; // Red - Below Average
    };

    const getScoreLabel = (score) => {
        if (score >= 80) return 'Excellent';
        if (score >= 60) return 'Good';
        if (score >= 40) return 'Average';
        return 'Needs Improvement';
    };

    const renderScoreCircle = (score) => {
        const radius = 54;
        const circumference = 2 * Math.PI * radius;
        const offset = circumference - (score / 100) * circumference;
        const scoreColor = getScoreColor(score);

        return (
            <div className="ai-score-circle-container">
                <svg className="ai-score-circle" width="140" height="140">
                    {/* Background circle */}
                    <circle
                        className="ai-score-circle-bg"
                        cx="70"
                        cy="70"
                        r={radius}
                        fill="none"
                        stroke="#eaf0f6"
                        strokeWidth="12"
                    />
                    {/* Progress circle */}
                    <circle
                        className="ai-score-circle-progress"
                        cx="70"
                        cy="70"
                        r={radius}
                        fill="none"
                        stroke={scoreColor}
                        strokeWidth="12"
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                        transform="rotate(-90 70 70)"
                    />
                </svg>
                <div className="ai-score-value" style={{ color: scoreColor }}>
                    {score}
                </div>
                <div className="ai-score-label" style={{ color: scoreColor }}>
                    {getScoreLabel(score)}
                </div>
            </div>
        );
    };

    let parsedEvals = [];
    if (callStatus?.questionEvaluations) {
        try {
            parsedEvals = typeof callStatus.questionEvaluations === 'string'
                ? JSON.parse(callStatus.questionEvaluations)
                : callStatus.questionEvaluations;
        } catch (e) {
            console.error('Error parsing question evaluations', e);
        }
    }

    return (
        <div className="call-status-modal-overlay">
            <div className="call-status-modal-container">
                <div className="call-status-modal-header">
                    <h2>AI Call Status</h2>
                    <button className="call-status-close-btn" onClick={onClose}>
                        ×
                    </button>
                </div>

                {isLoading ? (
                    <div className="call-status-loading">
                        <div className="call-status-spinner"></div>
                        <p>Loading call status...</p>
                    </div>
                ) : callStatus ? (
                    <>
                        {/* Status Badge */}
                        <div className="call-status-badge-container">
                            <span
                                className="call-status-badge"
                                style={{ backgroundColor: getStatusColor(callStatus.callStatus) }}
                            >
                                {getStatusLabel(callStatus.callStatus)}
                            </span>
                        </div>

                        {/* Tabs */}
                        <div className="call-status-tabs">
                            <button
                                className={`call-status-tab ${activeTab === 'details' ? 'active' : ''}`}
                                onClick={() => setActiveTab('details')}
                            >
                                Call Details
                            </button>
                            <button
                                className={`call-status-tab ${activeTab === 'score' ? 'active' : ''}`}
                                onClick={() => setActiveTab('score')}
                            >
                                AI Score
                            </button>
                            {(callStatus.recordingPath || callStatus.recordingUrl) && (
                                <button
                                    className={`call-status-tab ${activeTab === 'recording' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('recording')}
                                >
                                    🎙️ Recording
                                </button>
                            )}
                        </div>

                        <div className="call-status-content">
                            {/* Details Tab */}
                            {activeTab === 'details' && (
                                <>
                                    {/* Basic Info */}
                                    <div className="call-status-section">
                                        <h3>Call Details</h3>
                                        <div className="call-status-info-grid">
                                            <div className="call-status-info-item">
                                                <span className="call-status-label">Candidate ID</span>
                                                <span className="call-status-value">{callStatus.candidateId || '-'}</span>
                                            </div>
                                            <div className="call-status-info-item">
                                                <span className="call-status-label">Phone Number</span>
                                                <span className="call-status-value">{callStatus.phoneNumber || '-'}</span>
                                            </div>
                                            <div className="call-status-info-item">
                                                <span className="call-status-label">Call Duration</span>
                                                <span className="call-status-value">{formatDuration(callStatus.callLength)}</span>
                                            </div>
                                            <div className="call-status-info-item">
                                                <span className="call-status-label">Date Created</span>
                                                <span className="call-status-value">{formatDate(callStatus.dateCreated)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Error Message (if failed) */}
                                    {callStatus.callStatus?.toLowerCase() === 'failed' && callStatus.errorMessage && (
                                        <div className="call-status-section call-status-error-section">
                                            <h3>Error Details</h3>
                                            <div className="call-status-error">
                                                <span className="call-status-error-code">Error Code: {callStatus.errorCode}</span>
                                                <p className="call-status-error-message">{callStatus.errorMessage}</p>
                                            </div>
                                        </div>
                                    )}

                                    <>
                                        {/* Legacy Questions */}
                                        {callStatus.candidateQuestions && (
                                            <div className="call-status-section">
                                                <h3>Questions Asked</h3>
                                                <p className="call-status-text">{callStatus.candidateQuestions}</p>
                                            </div>
                                        )}

                                        {/* Legacy Evaluation Criteria */}
                                        {callStatus.evaluationCriteria && (
                                            <div className="call-status-section">
                                                <h3>Evaluation Criteria</h3>
                                                <p className="call-status-text">{callStatus.evaluationCriteria}</p>
                                            </div>
                                        )}
                                    </>

                                    {/* Transcript */}
                                    {callStatus.transcript && (
                                        <div className="call-status-section">
                                            <h3>Transcript</h3>
                                            <div className="call-status-transcript">{callStatus.transcript}</div>
                                        </div>
                                    )}

                                    {/* Summary */}
                                    {callStatus.summary && (
                                        <div className="call-status-section">
                                            <h3>Summary</h3>
                                            <p className="call-status-text">{callStatus.summary}</p>
                                        </div>
                                    )}

                                    {/* Sync Recording Section - show when call completed but no recording */}
                                    {callStatus.callStatus?.toLowerCase() === 'completed' &&
                                        !callStatus.recordingPath &&
                                        !callStatus.recordingUrl && (
                                            <div className="call-status-section sync-recording-section">
                                                <h3>📼 Recording</h3>
                                                <div className="sync-recording-content">
                                                    <p className="sync-recording-message">
                                                        Recording not available. Click below to sync recording from the call server.
                                                    </p>
                                                    {syncError && (
                                                        <p className="sync-recording-error">{syncError}</p>
                                                    )}
                                                    <button
                                                        className="sync-recording-btn"
                                                        onClick={handleSyncRecording}
                                                        disabled={isSyncingRecording}
                                                    >
                                                        {isSyncingRecording ? (
                                                            <>
                                                                <span className="sync-spinner"></span>
                                                                Syncing...
                                                            </>
                                                        ) : (
                                                            '🔄 Sync Recording'
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        )}


                                </>
                            )}

                            {/* AI Score Tab */}
                            {activeTab === 'score' && (
                                <div className="ai-score-tab-content">
                                    {callStatus.geminiScore !== null && callStatus.geminiScore !== undefined ? (
                                        <>
                                            {/* Score Display */}
                                            <div className="ai-score-display">
                                                {renderScoreCircle(callStatus.geminiScore)}
                                                <div className="ai-score-out-of">
                                                    <span>out of 100</span>
                                                </div>
                                            </div>

                                            {/* Feedback Section */}
                                            {parsedEvals.length > 0 ? (
                                                <div className="call-status-section">
                                                    <h3>Question Evaluations</h3>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                        {parsedEvals.map((ev, index) => (
                                                            <div
                                                                key={index}
                                                                style={{
                                                                    padding: '12px',
                                                                    background: '#f9f9f9',
                                                                    borderRadius: '8px',
                                                                    borderLeft: `4px solid ${ev.excludeFromScoring ? '#99a4af' : (ev.score !== undefined && ev.score !== null ? getScoreColor(ev.score) : '#ccc')}`
                                                                }}
                                                            >
                                                                <div style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'flex-start',
                                                                    gap: '12px',
                                                                    marginBottom: '8px'
                                                                }}>
                                                                    <span style={{ fontWeight: '600', color: '#2d3e50' }}>
                                                                        Q{index + 1}: {ev.question}
                                                                    </span>

                                                                    {ev.score !== undefined && ev.score !== null && !ev.excludeFromScoring && (
                                                                        <span style={{
                                                                            backgroundColor: getScoreColor(ev.score),
                                                                            color: '#fff',
                                                                            padding: '4px 8px',
                                                                            borderRadius: '4px',
                                                                            fontSize: '12px',
                                                                            fontWeight: 'bold',
                                                                            whiteSpace: 'nowrap'
                                                                        }}>
                                                                            Score: {ev.score}
                                                                        </span>
                                                                    )}
                                                                    {ev.excludeFromScoring && (
                                                                        <span className="ai-score-tooltip-container">
                                                                            <svg className="ai-info-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                                <circle cx="12" cy="12" r="10"></circle>
                                                                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                                                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                                                            </svg>
                                                                            <span className="ai-score-tooltip-text">Information Only</span>
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                <div style={{
                                                                    fontSize: '13px',
                                                                    color: '#516f90',
                                                                    marginBottom: '8px'
                                                                }}>
                                                                    <strong style={{ color: '#2d3e50' }}>Criteria:</strong>{' '}
                                                                    {ev.criteria || ev.evaluationCriteria}
                                                                </div>

                                                                {ev.feedback && (
                                                                    <div style={{
                                                                        fontSize: '13px',
                                                                        color: '#516f90',
                                                                        backgroundColor: '#eaf0f6',
                                                                        padding: '8px',
                                                                        borderRadius: '6px'
                                                                    }}>
                                                                        <strong style={{ color: '#2d3e50' }}>Feedback:</strong>{' '}
                                                                        {ev.feedback}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="ai-score-feedback">
                                                    No question evaluations available.
                                                </div>
                                            )}


                                            {/* Score Breakdown Legend */}
                                            <div className="ai-score-legend">
                                                <div className="ai-score-legend-item">
                                                    <span className="ai-score-legend-dot" style={{ backgroundColor: '#3BB765' }}></span>
                                                    <span>80-100: Excellent</span>
                                                </div>
                                                <div className="ai-score-legend-item">
                                                    <span className="ai-score-legend-dot" style={{ backgroundColor: '#FFB700' }}></span>
                                                    <span>60-79: Good</span>
                                                </div>
                                                <div className="ai-score-legend-item">
                                                    <span className="ai-score-legend-dot" style={{ backgroundColor: '#FF8C00' }}></span>
                                                    <span>40-59: Average</span>
                                                </div>
                                                <div className="ai-score-legend-item">
                                                    <span className="ai-score-legend-dot" style={{ backgroundColor: '#F35824' }}></span>
                                                    <span>0-39: Needs Improvement</span>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="ai-score-empty">
                                            <div className="ai-score-empty-icon">📊</div>
                                            <p>AI Score not available yet.</p>
                                            <span className="ai-score-empty-hint">
                                                Score will be generated once the call is completed.
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Recording Tab */}
                            {activeTab === 'recording' && (
                                <div className="recording-tab-content">
                                    {isLoadingAudio ? (
                                        <div className="recording-loading">
                                            <div className="call-status-spinner"></div>
                                            <p>Loading recording...</p>
                                        </div>
                                    ) : audioUrl ? (
                                        <div className="recording-player">
                                            <div className="recording-header">
                                                <div className="recording-icon">🎙️</div>
                                                <h3>Call Recording</h3>
                                            </div>
                                            <audio
                                                controls
                                                src={audioUrl}
                                                className="recording-audio-player"
                                            />
                                            <div className="recording-info">
                                                <div className="recording-info-item">
                                                    <span className="recording-info-label">Format</span>
                                                    <span className="recording-info-value">MP3</span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="recording-empty">
                                            <div className="recording-empty-icon">🎙️</div>
                                            <p>Unable to load recording</p>
                                            <button
                                                className="recording-retry-btn"
                                                onClick={loadAudioRecording}
                                            >
                                                Try Again
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="call-status-empty">
                        <p>No call status available.</p>
                    </div>
                )}

                {/* Actions */}
                <div className="call-status-modal-actions">
                    {callStatus && ['queued', 'in_progress'].includes(callStatus.callStatus?.toLowerCase()) && (
                        <button className="call-status-refresh-btn" onClick={onRefresh} disabled={isLoading}>
                            🔄 Refresh Status
                        </button>
                    )}
                    <button className="call-status-close-action-btn" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CallStatusModal;